import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Moon, Sun, Instagram, Facebook, Youtube } from 'lucide-react';
import { Button } from './ui/button';
import { socialLinks } from '../mock';
import { useTranslation } from '../i18n/LanguageContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, lang, setLang } = useTranslation();

  // Smart dark mode: detect system preference or use saved preference
  const getInitialDarkMode = () => {
    // Check if user has a saved preference
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode !== null) {
      return savedMode === 'true';
    }
    // Otherwise, detect system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  const [darkMode, setDarkMode] = useState(getInitialDarkMode);

  // Apply dark mode on mount and when it changes
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    // Save user preference
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('darkMode', darkMode.toString());
    }
  }, [darkMode]);

  // Listen for system theme changes (optional - updates automatically if user changes system theme)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only auto-update if user hasn't manually set a preference
      if (typeof window !== 'undefined' && window.localStorage) {
        const savedMode = window.localStorage.getItem('darkMode');
        if (savedMode === null) {
          setDarkMode(e.matches);
        }
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const toggleLang = () => {
    setLang(lang === 'en' ? 'es' : 'en');
  };

  const navLinks = [
    { key: 'home', path: '/' },
    { key: 'about', path: '/about' },
    { key: 'leadership', path: '/leadership' },
    { key: 'events', path: '/events' },
    { key: 'getInvolved', path: '/get-involved' },
    { key: 'give', path: '/give' },
    { key: 'contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="/images/logo.png" 
              alt="NPCMOP Logo" 
              className="h-16 w-16 object-contain drop-shadow-lg brightness-0 dark:brightness-100"
            />
            <div className="flex flex-col">
              <span className="text-2xl font-black tracking-tight text-gradient">NPCMOP</span>
              <span className="text-[10px] font-semibold text-muted-foreground hidden sm:block">Messengers of Peace</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground/80 hover:text-foreground hover:bg-secondary'
                }`}
              >
                {t(`nav.${link.key}`)}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-3">
            <a
              href={socialLinks.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full hover:bg-secondary transition-colors"
              aria-label="Instagram"
            >
              <Instagram className="h-5 w-5" />
            </a>
            <a
              href={socialLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full hover:bg-secondary transition-colors"
              aria-label="Facebook"
            >
              <Facebook className="h-5 w-5" />
            </a>
            <a
              href={socialLinks.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full hover:bg-secondary transition-colors"
              aria-label="YouTube"
            >
              <Youtube className="h-5 w-5" />
            </a>
            <div className="w-px h-6 bg-border"></div>
            <button
              onClick={toggleLang}
              className="px-2.5 py-1 rounded-full border border-border text-xs font-semibold uppercase tracking-[0.15em] hover:bg-secondary transition-colors"
              aria-label="Toggle language"
              data-testid="lang-toggle"
            >
              {lang === 'en' ? 'ES' : 'EN'}
            </button>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleDarkMode}
              className="rounded-full"
            >
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center space-x-2">
            <button
              onClick={toggleLang}
              className="px-2.5 py-1 rounded-full border border-border text-xs font-semibold uppercase tracking-[0.15em] hover:bg-secondary transition-colors"
              aria-label="Toggle language"
              data-testid="lang-toggle"
            >
              {lang === 'en' ? 'ES' : 'EN'}
            </button>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleDarkMode}
              className="rounded-full"
            >
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-1 fade-in">
            {/* Mobile Social Icons */}
            <div className="flex items-center justify-center space-x-4 py-4 border-b">
              <a
                href={socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full hover:bg-secondary transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href={socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full hover:bg-secondary transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href={socialLinks.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full hover:bg-secondary transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="h-5 w-5" />
              </a>
            </div>
            
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-md text-base font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground/80 hover:text-foreground hover:bg-secondary'
                }`}
              >
                {t(`nav.${link.key}`)}
              </Link>
            ))}
            <div className="px-4 pt-2">
              <Button className="w-full btn-hover">{t('common.joinNow')}</Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
